import React, { useEffect, useState } from "react";
import { Select } from "@hopper/select";
import { useIntegrationApp } from "@integration-app/react";
import { CSVTestCases } from "./test_cases_import";
import TestCasesUrl from "./test_cases_import/test_cases_url";
import { useFeatureFlag } from "app/feature_flags";
import { useField } from "formik";
import { TestCase } from "types/test_cases";
import { DISPLAY_NOTIFICATION, notificationEventBus } from "app/notificationsEventBus";
import { Connection } from "@integration-app/sdk";
import { StatusBar } from "@hopper/status_bar";
import { websiteContentPaths } from "components/configuration";
import { TestCycle, Project } from "./test_cases_import/integration_app";
import TestCasesImportForm from "./test_cases_import/test_cases_import_form";
import { Loader } from "@hopper/loading";

const integrationsKeyComponentMap: { [key: string]: JSX.Element } = {
  "zephyr-scale": <TestCasesImportForm integrationCode="zephyr-scale" />,
  "zephyr-squad-legacy": <TestCasesImportForm integrationCode="zephyr-squad-legacy" />,
  csv: <CSVTestCases />,
  url: <TestCasesUrl />,
};

const supportedIntegrationAppIntegrations = [
  {
    value: "zephyr-scale",
    label: "Zephyr Scale",
  },
  {
    value: "zephyr-squad-legacy",
    label: "Zephyr Squad",
  },
];

const TestCaseFields = () => {
  const [testCasesSource, , { setValue: setTestCasesSource }] = useField<string>("testCasesSource");
  const [testCasesUrl, _metaProps, _helperProps] = useField<string>("testCasesUrl");
  const [, , { setValue: setTestCases }] = useField<Array<TestCase> | undefined>("testCases");
  const [, , { setValue: setTestCycles }] = useField<Array<TestCycle>>("testCycles");
  const [, , { setValue: setProjects }] = useField<Array<Project>>("projects");
  const integrationAppEnabled = useFeatureFlag("integration-app", false);
  const integrationApp = useIntegrationApp();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);

  const fetchConnections = () => {
    integrationApp.connections
      .find()
      .then(response => {
        setConnections(response.items);
        setConnectionsLoaded(true);
      })
      .catch(() => {
        notificationEventBus.dispatch(DISPLAY_NOTIFICATION, {
          type: "danger",
          message: "There was an issue with connecting to some integrations, please try again later.",
        });
        setConnectionsLoaded(true);
      });
  };

  const activeIntegrationsList = supportedIntegrationAppIntegrations.filter(integration =>
    connections.some(connection => connection?.integration?.key === integration.value),
  );

  useEffect(() => {
    if (testCasesUrl.value && testCasesUrl.value.trim() !== "") {
      setTestCasesSource("url");
    }
  }, [testCasesUrl.value]);

  useEffect(() => {
    if (integrationAppEnabled) {
      fetchConnections();
    }
  }, [integrationAppEnabled]);

  if (integrationAppEnabled && !connectionsLoaded) {
    return <Loader />;
  }

  return integrationAppEnabled && connectionsLoaded ? (
    <div>
      <div className="mb-xl">
        <div className="relative border border-light bg-light p-lg leading-150 mb-xl">
          <StatusBar position="left" size="sm" />
          In order to add more test case sources please use the{" "}
          <a href={websiteContentPaths.integrationSettings} target="_blank" className="text-link underline ">
            Integrations settings
          </a>
        </div>
        <div className="text-sm mb-xs">Please select the source of test cases</div>
        <Select
          onChange={event => {
            setTestCasesSource(event.target.value);
            setTestCases(undefined);
            setTestCycles([]);
            setProjects([]);
          }}
          size="md"
          data-testid="test-cases-source"
          selectedValue={testCasesSource.value}
          options={[{ value: "url", label: "URL" }, { value: "csv", label: "CSV" }, ...activeIntegrationsList]}
        />
      </div>
      <div>{integrationsKeyComponentMap[testCasesSource.value]}</div>
    </div>
  ) : (
    <CSVTestCases />
  );
};

export default TestCaseFields;
